import { Grid, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { StyledReportBox, StyledReportBoxTitle, StyledReportPaperBox } from './report.styled'
import { faLongArrowRight, IconDefinition } from '@fortawesome/pro-solid-svg-icons'
import ReportBigButton from './components/ReportBigButton/ReportBigButton'
import { getTemplateSettings } from 'pages/deviation/pages/helpers/templateSettings'
import { useHistory } from "react-router-dom"
import { allRoutes } from 'generated/routes'
import { hasRole } from "auth/hasRole"
import { AppContext } from "components/AppContextProvider"
import { allRoles } from 'generated/roles'
import React, { useContext, useEffect, useState } from 'react'
import { IApiFunc, IApiResult } from "auth/interface/api.interface"
import { useApi } from "auth/useApi3"
import { apiEndpoints } from "generated/apiEndpoints";
import { IFormTemplateMini } from "components/Form/FormTemplate/formtemplate.interface"
import { FormTemplateKey } from "helpers/FormTemplate/FormTemplateKey"

interface IReportProps {
    activeCasesResult: any
}

function getFormTemplate(deviationsResult: IApiResult, templateKey: string) {
    return deviationsResult?.data?.find((template: IFormTemplateMini) => template.key === templateKey)
}

const getFormTemplateLink = (deviationsResult: IApiResult, templateKey: string) => {
    const formTemplate = getFormTemplate(deviationsResult, templateKey)
    return `${allRoutes.Deviation()}?id=${formTemplate?.id}`
}

const getEnabledFormTemplates = (
  customerResult: IApiResult,
  formTemplateResult: IApiResult
): IFormTemplateMini[] => {
    if (!customerResult?.data?.enabledFeatures || !formTemplateResult?.data) {
        return []
    }

    const enabledFeatures = customerResult.data.enabledFeatures
    return formTemplateResult.data.filter((template: any) => {
        if (!template.childFeatures || !Array.isArray(template.childFeatures)) {
            return false
        }

        return template.childFeatures.some((feature: string) =>
          enabledFeatures.includes(feature)
        )
    })
}

interface DashboardLink {
    key: string;
    iconEnd: IconDefinition;
    handleClick: () => any;
    role: string
}

const getReportLinks = (history: any, formTemplateResult: IApiResult): DashboardLink[] => [
    {
        key: "issue_reports",
        iconEnd: faLongArrowRight,
        handleClick: () => history.push(allRoutes.IssuereportsDashboard()),
        role: allRoles.IssueReports
    },
    {
        key: FormTemplateKey.VehicleDamage,
        iconEnd: faLongArrowRight,
        handleClick: () => history.push(getFormTemplateLink(formTemplateResult, FormTemplateKey.VehicleDamage)),
        role: allRoles.Deviation
    },
    {
        key: FormTemplateKey.ThreatsAndViolence,
        iconEnd: faLongArrowRight,
        handleClick: () => history.push(getFormTemplateLink(formTemplateResult, FormTemplateKey.ThreatsAndViolence)),
        role: allRoles.Deviation
    },
    {
        key: FormTemplateKey.WorkingEnvironment,
        iconEnd: faLongArrowRight,
        handleClick: () => history.push(getFormTemplateLink(formTemplateResult, FormTemplateKey.WorkingEnvironment)),
        role: allRoles.Deviation
    },
    {
        key: FormTemplateKey.TrafficAndCustomerInfo,
        iconEnd: faLongArrowRight,
        handleClick: () => history.push(getFormTemplateLink(formTemplateResult, FormTemplateKey.TrafficAndCustomerInfo)),
        role: allRoles.Deviation
    },
    {
        key: FormTemplateKey.TrafficLog,
        iconEnd: faLongArrowRight,
        handleClick: () => history.push(getFormTemplateLink(formTemplateResult, FormTemplateKey.TrafficLog)),
        role: allRoles.Deviation
    },
    {
        key: FormTemplateKey.VastanhedePersonalInjuries,
        iconEnd: faLongArrowRight,
        handleClick: () => history.push(getFormTemplateLink(formTemplateResult, FormTemplateKey.VastanhedePersonalInjuries)),
        role: allRoles.Deviation
    },
    {
        key: FormTemplateKey.VastanhedeTrafficDisruption,
        iconEnd: faLongArrowRight,
        handleClick: () => history.push(getFormTemplateLink(formTemplateResult, FormTemplateKey.VastanhedeTrafficDisruption)),
        role: allRoles.Deviation
    },
    {
        key: FormTemplateKey.VastanhedeWorkingEnvironment,
        iconEnd: faLongArrowRight,
        handleClick: () => history.push(getFormTemplateLink(formTemplateResult, FormTemplateKey.VastanhedeWorkingEnvironment)),
        role: allRoles.Deviation
    }
]

const Report: React.FC<IReportProps> = ({ activeCasesResult }) => {
    const [formTemplateResult, formTemplateApi] = useApi() as [IApiResult, IApiFunc]
    const { t } = useTranslation()
    const history = useHistory()
    const appContext = useContext(AppContext)
    const [customerResult, customerApi] = useApi() as [IApiResult, IApiFunc]
    const [links, setLinks] = useState<DashboardLink[]>()

    useEffect(() => {
        customerApi.get(apiEndpoints.customeradmin.getcustomer)
        formTemplateApi.get(apiEndpoints.form.getformtemplatesminimized)
    }, [])

    useEffect(() => {
        if (formTemplateResult.status !== 2 || customerResult.status !== 2) return
        const enabledTemplates = getEnabledFormTemplates(customerResult, formTemplateResult)
            .sort((a: any, b: any) => (a.customerId ? -1 : 1) - (b.customerId ? -1 : 1))

        const reportLinks = getReportLinks(history, formTemplateResult)
        const enabledFormTemplates = reportLinks.filter(link => enabledTemplates.some((template: any) => template.key === link.key))
        setLinks([ reportLinks[0], ...enabledFormTemplates ])
    }, [formTemplateResult, customerResult])

    return (
        <>
            <StyledReportPaperBox>
                <Grid direction="column" container>
                    {hasRole(appContext.appUser, allRoles.IssueReports) || hasRole(appContext.appUser, allRoles.Deviation) ?
                        <>
                            <Grid item xs={12} mb={2}>
                                <Typography color="textSecondary" variant="labelSmall">{t("dashboard.my_ongoing_matter", "My ongoing matters")}</Typography>
                            </Grid>
                            <Grid container spacing={2}>
                                {hasRole(appContext.appUser, allRoles.IssueReports) &&
                                    <Grid item xs={6} md={3} lg={6} xl={4}>
                                        <StyledReportBox onClick={() => history.push(allRoutes.IssuereportsArchive())} activeData={activeCasesResult?.data?.issueReports}>
                                            <StyledReportBoxTitle activeData={activeCasesResult?.data?.issueReports}>{activeCasesResult?.data?.issueReports}</StyledReportBoxTitle>
                                            <Typography variant="labelSmall" color="textSecondary">{t("global.features.title.issuereports", "Issue reports")}</Typography>
                                        </StyledReportBox>
                                    </Grid>}
                                {hasRole(appContext.appUser, allRoles.Deviation) &&
                                    <Grid item xs={6} md={3} lg={6} xl={4}>
                                        <StyledReportBox onClick={() => history.push(allRoutes.DeviationList())} activeData={activeCasesResult?.data?.deviations}>
                                            <StyledReportBoxTitle activeData={activeCasesResult?.data?.deviations}>{activeCasesResult?.data?.deviations}</StyledReportBoxTitle>
                                            <Typography variant="labelSmall" color="textSecondary">{t("global.features.title.deviation", "Deviation")}</Typography>
                                        </StyledReportBox>
                                    </Grid>}
                                {hasRole(appContext.appUser, allRoles.Violation) &&
                                    <Grid item xs={6} md={3} lg={6} xl={4}>
                                        <StyledReportBox onClick={() => history.push(allRoutes.Violation())} activeData={activeCasesResult?.data?.violations}>
                                            <StyledReportBoxTitle activeData={activeCasesResult?.data?.violations}>{activeCasesResult?.data?.violations}</StyledReportBoxTitle>
                                            <Typography variant="labelSmall" color="textSecondary">{t("global.features.title.violation", "Violation")}</Typography>
                                        </StyledReportBox>
                                    </Grid>}
                            </Grid>
                        </>
                        : null}
                    <Grid item xs={12} pt={5}>
                        <Grid container spacing={1} display="flex" flexDirection="column">
                            <Grid item mb={1}>
                                <Typography color="textSecondary" variant="labelSmall">{t("dashboard.create_new_report", "Create new report")}</Typography>
                            </Grid>

                            {links?.map((link, index) => {
                                const settings = getTemplateSettings(link.key);
                                const label = settings?.label || "";
                                const defaultValue = settings?.defaultValue || "";
                                return (
                                    hasRole(appContext.appUser, link.role) && (
                                        <Grid item xs={12} key={index}>
                                            <ReportBigButton
                                                iconStart={settings?.icon}
                                                label={t(label, defaultValue)}
                                                {...link}
                                            />
                                        </Grid>
                                    )
                                );
                            })}
                        </Grid>
                    </Grid>
                </Grid>
            </StyledReportPaperBox>
        </>
    )
}

export default Report
